import React from 'react';
import { Star, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import ScrollReveal from './ScrollReveal';
import StaggerContainer, { StaggerItem } from './StaggerContainer';

const TrustSection = () => {
  const testimonials = [
    {
      text: 'Enkelt, hurtig og lav risiko.',
      author: 'Kim',
      rating: 5
    },
    {
      text: 'Alt iht det de skriver at de tilbyr.',
      author: 'Christian',
      rating: 5
    },
    {
      text: 'Pengene på konto 6 timer etter signert salgsmelding.',
      author: 'Ronny',
      rating: 5
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-15 translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <ScrollReveal direction="up" className="text-center mb-12">
          <motion.div 
            className="flex items-center justify-center gap-3 mb-3"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ 
              type: "spring",
              stiffness: 200,
              damping: 15
            }}
          >
            <motion.span 
              className="text-5xl font-black bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              4.7
            </motion.span>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: 0.3 + i * 0.05,
                    type: "spring",
                    stiffness: 200
                  }}
                >
                  <Star className="w-6 h-6 fill-gray-900 text-gray-900" />
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.p 
            className="text-gray-600 font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            Basert på 2 000+ vurderinger
          </motion.p>
        </ScrollReveal>

        <StaggerContainer staggerDelay={0.12} className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <StaggerItem key={index}>
              <motion.div 
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-sm h-full relative overflow-hidden hover-lift"
                whileHover={{ 
                  scale: 1.03,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  borderColor: "#fbbf24"
                }}
                transition={{ 
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }}
              >
                {/* Quote icon decoration */}
                <motion.div
                  className="absolute -top-2 -right-2 opacity-10"
                  initial={{ scale: 0, rotate: -45 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 + 0.3 }}
                >
                  <Quote className="w-20 h-20 text-gray-900" />
                </motion.div>

                {/* Stars with stagger animation */}
                <motion.div 
                  className="flex gap-1 mb-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.05,
                        delayChildren: index * 0.12 + 0.2
                      }
                    }
                  }}
                >
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      variants={{
                        hidden: { opacity: 0, scale: 0, y: 10 },
                        visible: { 
                          opacity: 1, 
                          scale: 1, 
                          y: 0,
                          transition: {
                            type: "spring",
                            stiffness: 300
                          }
                        }
                      }}
                    >
                      <Star className="w-5 h-5" style={{ fill: '#fefe94', color: '#fefe94' }} />
                    </motion.div>
                  ))}
                </motion.div>

                <p className="text-gray-900 font-medium mb-4 text-lg leading-relaxed relative z-10">
                  "{testimonial.text}"
                </p>
                <p className="text-sm font-semibold text-gray-600 relative z-10">
                  — {testimonial.author}
                </p>

                {/* Subtle shimmer effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                  whileHover={{ 
                    opacity: 0.3,
                    x: ['-100%', '100%']
                  }}
                  transition={{ duration: 0.8 }}
                />
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default TrustSection;